// Treasury → Revenue → Distributions (SM-310).
//
// The operational queue: commission that is OWED but has not posted.
// Everything here is money the platform has already collected from a
// trader and not yet passed to the admin who earned it, which is why
// the default filter is FAILED rather than "all" — pending usually
// just means the sweeper has not caught up, while failed means
// something needs a human.
//
// The retry story is deliberately NOT a button here. The sweeper
// retries on its own backoff and is idempotent on the charge id; a
// manual "retry" control would race it for no benefit. What an
// operator actually needs is the error text and the attempt count, so
// they can fix the CAUSE — a closed recipient account, a deleted admin
// — after which the sweeper completes on its own.
//
// ── the currency dimension (SM-498) ──────────────────────────────────
//
// A distribution has been denominated in exactly one currency since
// SM-435: a commission is a slice of a fee that was already collected,
// so it is owed in that fee's currency — GOLD for a CASH fee, RED for a
// GAME buy-side fee, GREEN for a GAME sell-side one. The queue was
// drawn as if all of it were GOLD, which turned a page about money the
// platform owes into one that could show three currencies' worth of
// digits under a single ⚡ and let an operator settle the wrong wallet.
//
// Two rules follow, and everything below is one of them:
//
//   Every AMOUNT is rendered in its own row's currency. The fee wears
//   `currency`, the commission wears `commission_currency`. They are
//   equal on every row policy v1 can write, and they are still read
//   from separate fields, because the day they diverge the UI should
//   already be right rather than confidently wrong.
//
//   Nothing is ever summed across currencies (G8). The header count is
//   a count of rows, which is safe unscoped; the amount OWED is money,
//   so it appears only once the operator has named a currency. An
//   unscoped call to the summary endpoint does not answer "all
//   currencies" — settlement pins it to GOLD — so showing it beside
//   "All currencies" would be a lie with a number attached.
import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { CurrencyAmount, CurrencyBadge } from "@/components/ui/CurrencyAmount";
import { EmptyState } from "@/components/ui/EmptyState";
import { LoadFailed } from "@/components/fees/LoadFailed";
import { CurrencyFilter, parseCurrencyFilter } from "@/components/currency/CurrencyFilter";
import {
  REPORT_CURRENCY_DEFAULT,
  parseReportCurrency,
} from "@/components/currency/ReportCurrencySelect";
import {
  useCurrencyName,
  useCurrencyPresentation,
} from "@/components/currency/CurrencyPresentationProvider";
import { cn } from "@/lib/cn";
import { shortId } from "@/lib/format";
import { feeRoleLabel, feeRoleTone } from "@/components/fees/feeRole";
import { ChargeRate } from "@/components/commission/CommissionRateTriple";
import { commissionQueueDp } from "@/components/commission/commissionQueue";
import {
  commissionKeys,
  fetchCommissionCharges,
  fetchCommissionSummary,
  type CommissionCharge,
  type CommissionFilters,
} from "@/lib/queries";
import { useMyPerms } from "@/hooks/useMyPerms";
import { Perm } from "@/lib/perms";
import type { CurrencyCode } from "@/currency";
import { campaignCurrency } from "@/lib/currency";

/**
 * The currency lives in the URL, not in component state.
 *
 * A filtered view of this queue is something an operator pastes into an
 * incident ticket — "the RED distributions are all failing" — so it has
 * to survive a reload and the back button. Same reasoning as the stake
 * filter on Active positions (SM-490), and the same fail-open parse.
 */
export const Route = createFileRoute("/_app/treasury/revenue/distributions")({
  validateSearch: (raw: Record<string, unknown>): { currency?: CurrencyCode } => ({
    currency: parseCurrencyFilter(raw.currency),
  }),
  component: DistributionsPage,
});

// VOID (SM-562) is the third queue: commission that was priced but can
// never be owed, because the fee it was carved from was never collected —
// the fill's ledger debit conflicted and the position was withheld, or the
// redemption was abandoned. Terminal and settled by the service itself; it
// is listed here so the row an operator was watching does not simply
// vanish from Pending, and so the reason it left is one click away.
const STATES = [
  { value: "FAILED", label: "Failed" },
  { value: "PENDING", label: "Pending" },
  { value: "VOID", label: "Voided" },
];

const QUEUE_TITLE: Record<string, string> = {
  FAILED: "Failed distributions",
  PENDING: "Pending distributions",
  VOID: "Voided distributions",
};

// The row cap on one fetch. There is no pager here — the queue is meant
// to be emptied, not browsed — but a cap that is silently hit reads as
// "that is all of them", which is the one thing a queue must never say.
// See the note under the table.
const ROW_LIMIT = 200;

const TH = "px-3 py-3 text-2xs font-medium uppercase tracking-wide text-muted";

function DistributionsPage() {
  const perms = useMyPerms();
  const redacted = perms.loaded && !perms.has(Perm.CatalogViewAll);
  const [status, setStatus] = useState("FAILED");

  const { currency } = useSearch({ from: "/_app/treasury/revenue/distributions" });
  const navigate = useNavigate();
  const setCurrency = (next: CurrencyCode | undefined) =>
    navigate({ to: ".", search: (prev) => ({ ...prev, currency: next }), replace: true });
  const currencyName = useCurrencyName(currency);

  const filters: CommissionFilters = useMemo(
    () => ({ status, commissioned_only: "true", ...(currency ? { currency } : {}) }),
    [status, currency],
  );
  const q = useQuery({
    queryKey: commissionKeys.charges(filters, 0),
    queryFn: () => fetchCommissionCharges(filters, { limit: ROW_LIMIT }),
    // Action-oriented data uses the FAST tier: an operator working this
    // queue wants to see a row disappear when the sweeper clears it.
    refetchInterval: 10_000,
  });
  const rows = q.data?.charges ?? [];
  const total = q.data?.total ?? 0;
  const capped = total > rows.length;

  // How much money is stuck, in the currency the operator named. Only
  // asked for once a currency IS named: unscoped, settlement answers
  // this endpoint in GOLD alone (its historic meaning), and a GOLD
  // figure sitting under "All currencies" would understate the backlog
  // by however much RED and GREEN are in it.
  const owedQ = useQuery({
    queryKey: commissionKeys.summary(filters),
    queryFn: () => fetchCommissionSummary(filters),
    enabled: Boolean(currency),
    refetchInterval: 30_000,
  });
  // Nothing is owed on a voided row, so the "Owed in" line has no figure
  // to show under that queue: the summary endpoint has no void bucket
  // and inventing one from pending would misstate the backlog.
  const owed =
    status === "FAILED"
      ? owedQ.data?.commission_failed
      : status === "PENDING"
        ? owedQ.data?.commission_pending
        : undefined;
  // Hoisted so the status literal is compared once, here, and the JSX
  // below branches on a boolean.
  const voided = status === "VOID";
  const showOwed = Boolean(currency) && !voided;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Distributions</h1>
        <p className="max-w-[75ch] text-sm text-muted">
          Commission the platform has collected but not yet passed on. Each one
          is owed in the currency of the fee it was carved from, so amounts on
          this page are never added together. The sweeper retries automatically
          on a backoff — fix the underlying cause (a closed or missing admin
          account) and the retry completes on its own. Distributions whose
          source settlement failed for good are voided, not retried: the fee
          was never collected, so nothing is owed.{" "}
          <Link
            to="/treasury/revenue/commissions"
            // The report is an aggregate and therefore always has
            // exactly one currency; this queue may have none picked.
            // Hand over what the operator chose, and the report's own
            // default only when they chose nothing.
            search={{ currency: currency ?? REPORT_CURRENCY_DEFAULT }}
            className="text-accent hover:underline"
          >
            Full commission report
          </Link>
        </p>
      </div>

      <Card>
        <CardHeader className="flex-wrap gap-3">
          <div>
            <CardTitle>
              {QUEUE_TITLE[status] ?? "Distributions"}
              {q.data ? (
                <span className="ml-2 text-sm font-normal text-muted">
                  {total.toLocaleString()}
                </span>
              ) : null}
            </CardTitle>
            {/* The money, once there is a single currency to state it
                in. Rendered from the server's own aggregate — nothing
                on this page adds amounts, which is what keeps int64
                minor units exact. If that aggregate fails the line is
                simply absent: the queue itself is the page, and a
                broken side-figure is not worth an error beside it. */}
            {showOwed && (owedQ.isLoading || owed !== undefined) ? (
              // A <div>, not a <p>: <Skeleton> is a block element, and a
              // <div> inside a <p> is invalid HTML the browser silently
              // reparents — React logs it as a nesting error.
              <div className="mt-1 flex items-center gap-1.5 text-xs text-muted">
                <span>{`Owed in ${currencyName}`}</span>
                {owed !== undefined ? (
                  <CurrencyAmount
                    currency={currency}
                    value={owed}
                    className="font-medium text-t-2"
                  />
                ) : (
                  <Skeleton className="h-3 w-20" />
                )}
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-52">
              <CurrencyFilter value={currency} onChange={setCurrency} hideLabel />
            </div>
            <SegmentedControl
              options={STATES}
              value={status}
              onChange={setStatus}
            />
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {q.isLoading ? (
            <div className="space-y-2 p-5">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : q.isError ? (
            <div className="p-5">
              <LoadFailed
                what="the distribution queue"
                error={q.error}
                onRetry={() => void q.refetch()}
              />
            </div>
          ) : rows.length === 0 ? (
            // An empty page under a filter has two very different
            // meanings — nothing is owed at all, or nothing is owed in
            // THIS currency — and an operator who cannot tell them
            // apart will go looking for a bug in the sweeper.
            <EmptyState
              title={
                status === "FAILED"
                  ? "No failed distributions"
                  : voided
                    ? "No voided distributions"
                    : "Nothing pending"
              }
              description={
                voided
                  ? currency
                    ? `No ${currencyName} commission has been voided. Distributions are voided only when their source settlement failed for good. Other currencies are not shown under this filter.`
                    : "No commission has been voided. Distributions are voided only when their source settlement failed for good."
                  : currency
                    ? status === "FAILED"
                      ? `Every ${currencyName} commission the platform owes has posted to its recipient. Other currencies are not shown under this filter.`
                      : `The sweeper has no ${currencyName} backlog. Other currencies are not shown under this filter.`
                    : status === "FAILED"
                      ? "Every commission the platform owes has posted to its recipient."
                      : "The sweeper has no backlog."
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={cn(TH, "text-left")}>Charge</th>
                    <th className={cn(TH, "text-left")}>Source</th>
                    <th className={cn(TH, "text-left")}>Market</th>
                    <th className={cn(TH, "text-left")}>Recipient</th>
                    <th className={cn(TH, "text-left")}>Currency</th>
                    <th className={cn(TH, "text-right")}>Fee</th>
                    <th className={cn(TH, "text-right")}>Rate</th>
                    <th className={cn(TH, "text-right")}>Commission</th>
                    <th className={cn(TH, "text-right")}>Attempts</th>
                    <th className={cn(TH, "text-left")}>
                      {voided ? "Why voided" : "Why waiting"}
                    </th>
                    <th className={cn(TH, "text-right")}>Charged</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr
                      key={c.charge_id}
                      className="border-b border-border/60 last:border-0 hover:bg-surface-2/60"
                    >
                      <td className="px-3 py-2.5 font-mono text-2xs text-muted">
                        {c.charge_id}
                        {c.platform_credit_operation_id && (
                          <div
                            className="mt-0.5 text-[10px] text-t-4"
                            title={c.platform_credit_operation_id}
                          >
                            platform op · {shortId(c.platform_credit_operation_id)}
                          </div>
                        )}
                        {c.ledger_operation_id && (
                          <div className="text-[10px] text-t-4" title={c.ledger_operation_id}>
                            source op · {shortId(c.ledger_operation_id)}
                          </div>
                        )}
                      </td>
                      {/* What KIND of charge this is, and the id to
                          cross-reference it by. A redemption carries no
                          trade or order, so without this a support agent
                          reading the row has nothing to search on and no
                          way to tell it from a trading fee. */}
                      <td className="px-3 py-2.5">
                        <Badge tone={feeRoleTone(c.fee_role)}>{feeRoleLabel(c.fee_role)}</Badge>
                        {c.redemption_id && (
                          <div className="mt-0.5 font-mono text-2xs text-muted" title={c.redemption_id}>
                            {shortId(c.redemption_id)}
                          </div>
                        )}
                        {!c.redemption_id && c.trade_id && (
                          <div className="mt-0.5 space-y-0.5 font-mono text-[10px] text-muted">
                            <div title={c.trade_id}>trade · {shortId(c.trade_id)}</div>
                            {c.order_id && <div title={c.order_id}>order · {shortId(c.order_id)}</div>}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        {redacted ? (
                          <span className="font-mono text-2xs text-muted">
                            {c.market_id}
                          </span>
                        ) : (
                          <Link
                            to="/treasury/revenue/markets/$marketId"
                            params={{ marketId: c.market_id }}
                            // The ROW's own currency, not the page
                            // filter: opening a stuck GREEN
                            // distribution must land on that market's
                            // GREEN book even from an unscoped queue.
                            search={{
                              currency: parseReportCurrency(
                                c.commission_currency ?? c.currency,
                              ),
                            }}
                            className="font-mono text-2xs text-accent hover:underline"
                          >
                            {c.market_id}
                          </Link>
                        )}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-2xs">
                        {c.commission_recipient_admin_id || "—"}
                      </td>
                      {/* Its own column as well as its own marks on the
                          amounts. Under "All currencies" the marks are
                          what distinguish two adjacent rows, but a
                          scanning operator reads a column far faster
                          than three glyphs down a numeric edge. */}
                      <td className="px-3 py-2.5">
                        <CurrencyBadge currency={c.commission_currency ?? c.currency} />
                      </td>
                      {/* The fee is denominated in the CHANNEL's
                          currency; the commission in the currency it
                          was carved from. Each carries its own mark and
                          its own configured precision, so two rows in
                          different currencies can sit next to each
                          other without either being misread. */}
                      <td className="px-3 py-2.5 text-right">
                        <QueueAmount
                          currency={c.currency}
                          value={c.fee_amount}
                          tone="muted"
                        />
                      </td>
                      {/* The rate this charge was priced at. Since
                          SM-387 that is the rate for its OWN fee type,
                          so a row's gross, rate and commission
                          reconcile against each other without anyone
                          having to know which of the recipient's three
                          rates applied. */}
                      <td className="px-3 py-2.5 text-right text-2xs">
                        <ChargeRate bps={c.commission_bps} feeRole={c.fee_role} />
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <QueueAmount
                          currency={c.commission_currency ?? c.currency}
                          value={c.commission_amount}
                        />
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <AttemptsCell attempts={c.commission_attempts} />
                      </td>
                      <td className="max-w-[30ch] px-3 py-2.5">
                        <WaitingCell charge={c} />
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-2xs text-muted">
                        {new Date(c.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Say the cap out loud. Narrowing by currency is the
                  documented way to get under it, and an operator who
                  does not know they are looking at a truncated queue
                  will report the remainder as missing. */}
              {capped ? (
                <p className="border-t border-border px-3 py-2.5 text-2xs text-t-4">
                  {`Showing the first ${rows.length.toLocaleString()} of ${total.toLocaleString()} distributions in this queue. Narrow the filters to see the rest.`}
                </p>
              ) : null}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function QueueAmount({
  currency,
  value,
  tone,
}: {
  currency: string | undefined | null;
  value: string | number | undefined | null;
  tone?: "default" | "muted";
}) {
  const presentation = useCurrencyPresentation(campaignCurrency(currency));
  return (
    <CurrencyAmount
      currency={currency}
      value={value}
      tone={tone}
      dp={commissionQueueDp(value, presentation.decimals)}
    />
  );
}

const WAITING_REASON_COPY: Record<string, string> = {
  MISSING_RECIPIENT: "Recipient account is missing",
  SOURCE_SETTLEMENT_MISSING: "Source settlement record is missing",
  SOURCE_SETTLEMENT_PENDING: "Waiting for source settlement",
  SOURCE_SETTLEMENT_FAILED: "Source settlement failed",
  SOURCE_SETTLEMENT_ABANDONED: "Source settlement was abandoned",
  RETRY_BACKOFF: "Retry scheduled after backoff",
  RETRY_READY: "Ready for the next retry",
  AWAITING_SWEEP: "Eligible; waiting for the sweeper",
  RETRY_IN_PROGRESS: "Retry is in progress",
};

function WaitingCell({ charge }: { charge: CommissionCharge }) {
  // A voided row is terminal: nothing is waiting, so the service sends no
  // waiting reason. What it does send is the verdict — in last_error, so
  // the same field an operator reads on a failed row explains this one.
  if (charge.commission_status === "VOID") {
    return (
      <div className="space-y-0.5 text-2xs">
        <div className="flex items-center gap-1.5">
          <Badge tone="default">Voided</Badge>
          <span className="font-medium text-t-2">Source settlement failed; nothing is owed</span>
        </div>
        {charge.source_settlement_status && (
          <div className="text-muted">Source · {charge.source_settlement_status}</div>
        )}
        {charge.commission_last_error && (
          <div className="text-muted" title={charge.commission_last_error}>
            {charge.commission_last_error}
          </div>
        )}
      </div>
    );
  }
  const reason = charge.commission_waiting_reason
    ? WAITING_REASON_COPY[charge.commission_waiting_reason] ?? charge.commission_waiting_reason
    : "Waiting reason unavailable";
  const nextAttempt = charge.commission_next_attempt_at
    ? new Date(charge.commission_next_attempt_at).toLocaleString()
    : null;

  return (
    <div className="space-y-0.5 text-2xs">
      <div className="font-medium text-warning">{reason}</div>
      {charge.source_settlement_status && (
        <div className="text-muted">Source · {charge.source_settlement_status}</div>
      )}
      {nextAttempt && <div className="text-muted">Next attempt · {nextAttempt}</div>}
      {charge.commission_last_error && (
        <div className="truncate text-danger" title={charge.commission_last_error}>
          {charge.commission_last_error}
        </div>
      )}
    </div>
  );
}

// The sweeper gives up after 12 attempts and stops retrying. Past that
// the row will sit here forever unless a human acts, so it is called
// out rather than shown as just another number.
const MAX_ATTEMPTS = 12;

function AttemptsCell({ attempts }: { attempts: number }) {
  if (attempts >= MAX_ATTEMPTS) {
    return (
      <Badge tone="danger" title="Automatic retries exhausted — needs manual action">
        {attempts} — exhausted
      </Badge>
    );
  }
  return <span className="tabular-nums text-t-2">{attempts}</span>;
}
